import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getSubcontractor, updateSubcontractor, setSubcontractorStanding, getSubcontractorTaxId, addSubcontractorNote,
  uploadSubcontractorDocument, updateSubcontractorDocument, removeSubcontractorDocument, downloadSubcontractorDocument,
  uploadJobDocument, getCoiWording,
} from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { Icon, SegmentedControl, StateScreen } from '../components/ui';
import {
  STANDINGS, DOC_TYPES, standingOf, expiryStatus, coiState, jobDocNeeds, jobNeedsDocs, focusOfNeed, fmtDay, fmtMonthYear, todayYmd,
  lastJobOf,
} from '../lib/subcontractors';
import {
  card, label, StandingPill, StatusText, ModalShell, Field, ErrorBanner, PrimaryButton, SecondaryButton, SubStyles,
} from '../components/subs/SubUi';
import SubcontractorForm from '../components/subs/SubcontractorForm';
import { JobDocDialog } from '../components/subs/JobDocuments';
import { JobDocRows, CompanyDocRows, DocSection, OtherDocList } from '../components/subs/SubDocRows';
import CompanyDocDialog from '../components/subs/CompanyDocDialog';
import DocViewer from '../components/subs/DocViewer';
import KpiRow from '../components/ar/ArKpiRow';

/* ═══════════════════════════════════════════════════════════════════════════
   One subcontractor (Subcontractor Mgmt, Duncan 2026-10-20): its standing, and four tabs --
     · Info: main contact, address, tax ID (masked until asked for), licenses
     · Documents: its jobs' documents (Duncan 2026-10-22: each job's certificate of insurance and job site
       safety plan, "all the ones that have been in all the jobs"), then its company documents -- MSA, W-9,
       prequalification, licenses, general insurance and safety, other. An expiry turns amber 30 days out and
       red once it lapses; an older copy reads "superseded".
     · Job history: CE's subcontracts, and Summit's schedules -- the jobs with a task whose Responsible
       Party is this sub (the portal's assignments come later)
     · Notes: dated notes and incidents, newest first, who wrote each; standing changes land here too
   ═══════════════════════════════════════════════════════════════════════════ */

const muted = { fontSize: 13, color: 'var(--text-3)' };
const fmtWhen = (ts) => (ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/* ── what's on file ─────────────────────────────────────────────────────────────────────────── */

/* The sub's company documents, once each (Duncan 2026-10-22: an MSA and a W-9 when it's set up, sometimes a
   prequalification questionnaire): the current copy of each. */
const onFile = (documents) => {
  const current = (t) => documents.find((d) => !d.project_id && d.doc_type === t && d.current) || null;
  return { msa: current('msa'), w9: current('w9'), prequal: current('prequal') };
};

/* The jobs this sub owes documents to, and any job it has documents for (a finished job with none stays off the
   list): each with its documents, whether it's open (the sub is ON it -- picked on its schedule or under CE
   subcontract -- and it isn't done) and what it still needs. Open ones owing something first. */
const jobDocJobs = (jobs, documents, today) => {
  const byId = new Map();
  jobs.filter((j) => j.project_id && (j.picked || j.subcontracts.length)).forEach((j) => byId.set(j.project_id, {
    id: j.project_id, name: j.project_name, number: j.project_number, slug: j.slug, owner: j.owner, status: j.status,
    archived: j.archived, is_test: j.is_test, onJob: true,
  }));
  documents.filter((d) => d.project && !byId.has(d.project_id)).forEach((d) => byId.set(d.project_id, {
    id: d.project_id, name: d.project.name, number: d.project.number, slug: d.project.slug, owner: null, status: d.project.status,
    archived: d.project.archived, is_test: d.project.is_test, onJob: false,
  }));
  return [...byId.values()].map((j) => {
    const docs = documents.filter((d) => d.project_id === j.id);
    const open = j.onJob && jobNeedsDocs(j);
    return { ...j, docs, open, needs: open ? jobDocNeeds(docs, today) : [] };
  }).filter((j) => j.open || j.docs.length).sort((a, b) => (b.open - a.open) || ((b.needs.length > 0) - (a.needs.length > 0)) || String(a.name).localeCompare(String(b.name)));
};

/** The row the "Job documents" flag opens: the first open job still owing, at what it owes first ("job-12:coi"). */
const firstOwingKey = (jobDocs) => {
  const j = jobDocs.find((x) => x.open && x.needs.length) || jobDocs.find((x) => x.open);
  return j ? `job-${j.id}:${j.needs.length ? focusOfNeed(j.needs[0].key) : 'coi'}` : 'msa';
};

/* What's on file and what's missing. Each opens its row on the Documents tab, highlighted (Duncan 2026-10-22: a
   flag "should take you to the spot where to file those things"). Its jobs' documents are one more chip: how many
   open jobs still need theirs -- it opens the first one's. */
const RequiredChecklist = ({ f, jobDocs, onPick }) => {
  const open = jobDocs.filter((j) => j.open);
  const owing = open.filter((j) => j.needs.length);
  const red = owing.some((j) => j.needs.some((n) => n.tone === 'var(--danger)'));
  const items = [
    { key: 'msa', label: 'MSA', ok: !!f.msa, text: f.msa ? (f.msa.signed_date ? `signed ${fmtDay(f.msa.signed_date)}` : 'on file') : 'missing' },
    { key: 'w9', label: 'W-9', ok: !!f.w9, text: f.w9 ? 'on file' : 'missing' },
    { key: 'prequal', label: 'Prequalification', ok: !!f.prequal, optional: true, text: f.prequal ? 'on file' : 'none (optional)' },
    {
      key: 'jobs', label: 'Job documents', ok: !owing.length, warn: owing.length > 0 && !red, neutral: !open.length,
      text: !open.length ? 'not on an open job' : owing.length ? `${owing.length} of ${open.length} open job${open.length === 1 ? '' : 's'} missing` : `in order (${open.length})`,
    },
  ];
  return (
    <div aria-label="Required documents" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 16px 12px' }}>
      {items.map((it) => {
        const tone = it.neutral || (it.optional && !it.ok) ? 'var(--text-3)' : it.ok ? 'var(--success)' : it.warn ? 'var(--warning)' : 'var(--danger)';
        return (
          <button key={it.key} type="button" onClick={() => onPick(it.key)} title={`${it.label}: ${it.text}. Show it.`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px', borderRadius: 999, cursor: 'pointer',
              fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-sans)', color: tone, background: 'var(--bg-surface)',
              border: `1px solid color-mix(in oklch, ${tone} 35%, transparent)`,
            }}>
            <Icon name={it.ok && !it.neutral ? 'check' : 'alert'} size={13} />
            {it.label}<span style={{ fontWeight: 400, color: 'var(--text-3)' }}>{it.text}</span>
          </button>
        );
      })}
    </div>
  );
};

/* The insurance spot, by job now (Duncan 2026-10-22: a certificate is each job's): every open job's certificate
   and safety plan, and the way to its documents. */
const JobDocsSummary = ({ jobDocs, today, onSeeAll }) => {
  const open = jobDocs.filter((j) => j.open);
  return (
    <div style={{ gridColumn: '1 / -1', padding: '12px 14px', borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <span style={muted}>Insurance and safety plans, by job</span>
        <button type="button" onClick={onSeeAll} style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}>All job documents</button>
      </div>
      {open.length ? open.map((j) => {
        const coi = coiState(j.docs, today);
        const plan = j.docs.some((d) => d.doc_type === 'safety');
        return (
          <div key={j.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', padding: '4px 0', fontSize: 13.5, borderTop: '1px solid var(--border-subtle)' }}>
            <span style={{ flex: '1 1 220px', minWidth: 0, fontWeight: 600, color: 'var(--text-1)' }}>
              {j.name}{j.number && <span className="mono" style={{ fontWeight: 400, color: 'var(--text-3)' }}> #{j.number}</span>}
            </span>
            <StatusText status={{ text: coi.text, tone: coi.level === 'current' ? null : coi.tone }} />
            <StatusText status={plan ? { text: 'safety plan on file', tone: null } : { text: 'No safety plan', tone: 'var(--danger)' }} />
          </div>
        );
      }) : <div style={{ fontSize: 14, color: 'var(--text-3)' }}>Not on an open job.</div>}
    </div>
  );
};

/* ── Info ───────────────────────────────────────────────────────────────────────────────────── */

const InfoItem = ({ title, children }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ ...muted, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 14, color: 'var(--text-1)', lineHeight: 1.55 }}>{children}</div>
  </div>
);
const NotSet = ({ children = 'Not set' }) => <span style={{ color: 'var(--text-4)' }}>{children}</span>;

const InfoTab = ({ sub, documents, taxId, onToggleTaxId, taxBusy, jobDocs, onSeeJobDocs }) => {
  const today = todayYmd();
  const w9 = documents.some((d) => d.doc_type === 'w9');
  const licenses = documents.filter((d) => d.doc_type === 'license' && d.current);
  const address = [sub.address_line1, sub.address_line2, [sub.city, [sub.state, sub.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean);
  return (
    <div className="sub-info" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '18px 24px', padding: '16px 18px' }}>
      <JobDocsSummary jobDocs={jobDocs} today={today} onSeeAll={onSeeJobDocs} />
      <InfoItem title="Main contact">
        {sub.contact_name || sub.contact_phone || sub.contact_email ? (
          <>
            {sub.contact_name && <div style={{ fontWeight: 600 }}>{sub.contact_name}{sub.contact_title && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}> · {sub.contact_title}</span>}</div>}
            {sub.contact_phone && <div><a href={`tel:${sub.contact_phone}`} style={{ color: 'inherit' }}>{sub.contact_phone}</a></div>}
            {sub.contact_email && <div><a href={`mailto:${sub.contact_email}`} style={{ color: 'var(--accent)' }}>{sub.contact_email}</a></div>}
          </>
        ) : <NotSet />}
      </InfoItem>
      <InfoItem title="Address">
        {address.length ? address.map((l, i) => <div key={i}>{l}</div>) : <NotSet />}
      </InfoItem>
      <InfoItem title={`Tax ID (${w9 ? 'W-9 on file' : 'no W-9 on file'})`}>
        {sub.has_tax_id ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span className="mono">{taxId || sub.tax_id_masked}</span>
            <button type="button" onClick={onToggleTaxId} disabled={taxBusy} style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, color: 'var(--accent)', cursor: 'pointer' }}>
              {taxId ? 'Hide' : 'Show'}
            </button>
          </span>
        ) : <NotSet>Not on file</NotSet>}
      </InfoItem>
      <InfoItem title="Licenses">
        {licenses.length ? licenses.map((d) => (
          <div key={d.id}>
            {d.title}{d.number && <span className="mono" style={{ color: 'var(--text-2)' }}> #{d.number}</span>}
            {d.expiry_date && <> · <StatusText status={expiryStatus(d.expiry_date, today)} /></>}
          </div>
        )) : <NotSet>None on file</NotSet>}
      </InfoItem>
      {sub.ce_vendor_name && sub.ce_vendor_name !== sub.name && (
        <InfoItem title="Name in CE">{sub.ce_vendor_name}</InfoItem>
      )}
    </div>
  );
};

/* ── Documents ──────────────────────────────────────────────────────────────────────────────── */

const groupHead = { fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' };
const countPill = { minWidth: 20, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' };

/* Its jobs' documents (Duncan 2026-10-22: "we should see all the ones that have been in all the jobs"), job by
   job as rows: open jobs owing something first. A job it's no longer on keeps its documents, without Upload. */
const JobDocsSection = ({ jobDocs, today, wording, focus, onAddJobDoc, onReplaceJobDoc, onToggleCheck, onEditJobDoc, onOpenJob, onRemove, onView, onDownload }) => (
  <section aria-label="Job documents" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 2px' }}>
      <span style={groupHead}>Job documents</span>
      <span className="mono" style={countPill}>{jobDocs.length}</span>
      <span style={{ ...muted, flex: 1, minWidth: 200 }}>each job’s certificate of insurance and job site safety plan</span>
    </div>
    {jobDocs.length ? jobDocs.map((j) => {
      // A flag for this job ("job-12:coi") points at one of its rows.
      const mine = focus && focus.key.startsWith(`job-${j.id}:`) ? { key: focus.key.split(':')[1], seq: focus.seq } : null;
      return (
        <div key={j.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '2px 2px 6px' }}>
            <button type="button" onClick={() => onOpenJob(j)} title="Open the job's Subcontractors tab"
              style={{ background: 'none', border: 'none', padding: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>{j.name}</button>
            {j.number && <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>#{j.number}</span>}
            {j.is_test && <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--warning)', padding: '0 6px', borderRadius: 999, border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)' }}>Test</span>}
            <span style={{ ...muted, fontSize: 12.5 }}>{[j.archived ? 'Archived' : j.status, !j.onJob && 'no longer on this job'].filter(Boolean).join(' · ')}</span>
          </div>
          <DocSection title="For this job">
            <JobDocRows job={{ name: j.name, number: j.number, owner: j.owner, needs_papers: j.open }} docs={j.docs} today={today} wording={wording}
              canAdd={j.onJob} focus={mine} onAdd={(kind, file) => onAddJobDoc(j, kind, file)} onReplace={(doc) => onReplaceJobDoc(j, doc)}
              onToggleCheck={onToggleCheck} onEdit={(doc) => onEditJobDoc(j, doc)} onRemove={onRemove} onView={onView} onDownload={onDownload} />
          </DocSection>
        </div>
      );
    }) : (
      <div style={{ padding: '12px 16px', borderRadius: 12, border: '1px dashed var(--border-strong)', fontSize: 13.5, color: 'var(--text-3)' }}>
        Not on a job with a Summit page yet. A job shows here once the sub is picked on its schedule or has a CE subcontract on it.
      </div>
    )}
  </section>
);

// The company papers each have their row; these other kinds keep their cards.
const OTHER_KINDS = DOC_TYPES.filter((t) => !['msa', 'w9', 'prequal'].includes(t.value));
const newestFirst = (a, b) => (b.current === true) - (a.current === true)
  || String(b.signed_date || b.effective_date || b.created_at || '').localeCompare(String(a.signed_date || a.effective_date || a.created_at || '')) || b.id - a.id;

const DocumentsTab = ({ documents, company, jobDocs, wording, focus, onAdd, onAddJobDoc, onReplaceJobDoc, onToggleCheck, onEditJobDoc, onOpenJob, ...cardProps }) => {
  const today = todayYmd();
  const history = Object.fromEntries(['msa', 'w9', 'prequal'].map((k) => [k, documents.filter((d) => !d.project_id && d.doc_type === k).sort(newestFirst)]));
  const others = OTHER_KINDS.map((type) => ({ type, docs: documents.filter((d) => !d.project_id && d.doc_type === type.value).sort(newestFirst) })).filter((g) => g.docs.length);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 16px 18px' }}>
      <DocSection title="Company papers" note="once, for every job">
        <CompanyDocRows company={company} history={history} today={today} canAdd canOpen focus={focus} onAdd={onAdd}
          onEdit={cardProps.onEdit} onRemove={cardProps.onRemove} onView={cardProps.onView} onDownload={cardProps.onDownload} />
      </DocSection>
      <JobDocsSection jobDocs={jobDocs} today={today} wording={wording} focus={focus} onAddJobDoc={onAddJobDoc} onReplaceJobDoc={onReplaceJobDoc}
        onToggleCheck={onToggleCheck} onEditJobDoc={onEditJobDoc} onOpenJob={onOpenJob} onRemove={cardProps.onRemove} onView={cardProps.onView} onDownload={cardProps.onDownload} />
      <DocSection title="Other documents" note="licenses, general certificates and anything else"
        action={<SecondaryButton onClick={() => onAdd('license')} style={{ height: 28, padding: '0 10px', fontSize: 13 }}><Icon name="upload" size={13} />Add document</SecondaryButton>}>
        {others.length ? <OtherDocList groups={others} today={today} onView={cardProps.onView} onDownload={cardProps.onDownload} onEdit={cardProps.onEdit} onRemove={cardProps.onRemove} />
          : <div style={{ ...muted, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' }}>None on file</div>}
      </DocSection>
    </div>
  );
};

/* ── Job history ────────────────────────────────────────────────────────────────────────────── */

const JOB_COLS = 'minmax(0, 1.7fr) minmax(0, 1.1fr) minmax(0, 1.5fr) minmax(0, 0.7fr)';

/* A job's dates: its schedule's, else when it was subcontracted in CE. */
const jobDates = (j) => {
  if (j.first_start || j.last_finish) return `${j.first_start ? fmtDay(j.first_start) : '…'} → ${j.last_finish ? fmtDay(j.last_finish) : '…'}`;
  const dated = j.subcontracts.filter((c) => c.contract_date);
  if (!dated.length) return '—';
  return `Subcontracted ${fmtDay(dated[0].contract_date)}${dated.length > 1 ? ` (+${dated.length - 1})` : ''}`;
};

const JobsTab = ({ sub, jobs, onOpenProject }) => (
  <div>
    {jobs.length === 0 ? (
      <div style={{ padding: '4px 12px 14px' }}>
        <StateScreen kind="empty" title="No jobs yet" body={`Jobs show here from CE's subcontracts, and from Summit schedules where ${sub.name} is a task's Responsible Party.`} />
      </div>
    ) : (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: JOB_COLS, gap: 12, padding: '8px 16px', ...label, fontSize: 11 }}>
          <span>Job</span><span>Dates</span><span>Scope</span><span>Status</span>
        </div>
        {jobs.map((j) => {
          // A job with a Summit page opens it; a CE-only one has nowhere to go.
          const open = j.project_id ? () => onOpenProject(j.slug || j.project_id) : null;
          const scope = [
            j.subcontracts.length ? `Sub #${j.subcontracts.map((c) => c.number).join(', #')}` : null,
            j.tasks ? plural(j.tasks, 'task') : null,
          ].filter(Boolean).join(' · ');
          return (
            <div key={j.project_id || `ce-${j.project_number}-${j.subcontracts[0]?.number}`} className={open ? 'sub-row' : undefined}
              {...(open ? { role: 'button', tabIndex: 0, 'aria-label': `Open ${j.project_name}`, onClick: open, onKeyDown: (e) => { if (e.key === 'Enter') open(); } } : {})}
              style={{ display: 'grid', gridTemplateColumns: JOB_COLS, gap: 12, alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--border-subtle)', cursor: open ? 'pointer' : 'default', fontSize: 13.5 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.project_name}</span>
                  {j.is_test && <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--warning)', padding: '0 6px', borderRadius: 999, border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)', whiteSpace: 'nowrap' }}>Test</span>}
                </div>
                {j.project_number && <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>#{j.project_number}</span>}
              </div>
              <span style={{ color: 'var(--text-2)' }}>{jobDates(j)}</span>
              <span title={j.sample_tasks.join(', ')} style={{ color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {scope}{j.sample_tasks.length > 0 && <span style={{ color: 'var(--text-3)' }}> · {j.sample_tasks.join(', ')}{j.tasks > j.sample_tasks.length ? ', …' : ''}</span>}
              </span>
              <span style={{ color: j.project_id ? 'var(--text-2)' : 'var(--text-4)' }} title={j.project_id ? undefined : 'No Summit project page'}>
                {j.project_id ? (j.archived ? 'Archived' : j.status || '—') : 'CE only'}
              </span>
            </div>
          );
        })}
      </>
    )}
    <div style={{ ...muted, fontSize: 12.5, padding: '10px 16px 14px', borderTop: '1px solid var(--border-subtle)' }}>
      From CE's subcontracts, and Summit's schedules: the jobs with a task whose Responsible Party is {sub.name}.
    </div>
  </div>
);

/* ── Notes ──────────────────────────────────────────────────────────────────────────────────── */

const KIND = {
  incident: { text: 'Incident', tone: 'var(--danger)' },
  standing: { text: 'Standing', tone: 'var(--accent)' },
};

const NotesTab = ({ notes, onAdd }) => {
  const [kind, setKind] = useState('note');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const add = async () => {
    if (busy || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(kind, body.trim());
      setBody('');
      setKind('note');
    } catch (e) {
      setError(e.message || 'Failed to add the note');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 16px 16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div style={{ alignSelf: 'flex-start' }}>
          <SegmentedControl value={kind} onChange={setKind} options={[{ value: 'note', label: 'Note' }, { value: 'incident', label: 'Incident' }]} />
        </div>
        <Field id="sub-note" as="textarea" value={body} onChange={setBody} aria-label="Note"
          placeholder={kind === 'incident' ? 'What happened, on which job, and what was done about it' : 'A note about this subcontractor'} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryButton busy={busy} disabled={!body.trim()} onClick={add}>{busy ? 'Adding…' : kind === 'incident' ? 'Add incident' : 'Add note'}</PrimaryButton>
        </div>
      </div>
      {notes.length === 0 ? <div style={muted}>No notes yet.</div> : notes.map((n) => (
        <div key={n.id} style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            {KIND[n.kind] && <span style={{ fontSize: 11.5, fontWeight: 700, color: KIND[n.kind].tone, padding: '0 7px', borderRadius: 999, border: `1px solid color-mix(in oklch, ${KIND[n.kind].tone} 35%, transparent)` }}>{KIND[n.kind].text}</span>}
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{n.author_name || 'Someone'}</span>
            <span style={muted}>{fmtWhen(n.created_at)}</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-1)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.body}</div>
        </div>
      ))}
    </div>
  );
};

/* ── Standing ───────────────────────────────────────────────────────────────────────────────── */

const StandingDialog = ({ sub, onClose, onSubmit }) => {
  const [standing, setStanding] = useState(sub.standing === 'approved' ? 'probation' : 'approved');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const needsReason = standing !== 'approved';
  const submit = async () => {
    if (busy) return;
    if (standing === sub.standing) return setError(`They're already ${standingOf(standing).label}.`);
    if (needsReason && !reason.trim()) return setError(`Say why they're going to ${standingOf(standing).label}.`);
    setBusy(true);
    setError(null);
    try {
      await onSubmit(standing, reason.trim());
    } catch (e) {
      setError(e.message || 'Failed to change the standing');
      setBusy(false);
    }
  };
  return (
    <ModalShell title={`${sub.name}'s standing`} subtitle="The change and why go in their notes." onClose={onClose} busy={busy} width={480}
      footer={(
        <>
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy} onClick={submit}>{busy ? 'Saving…' : 'Change standing'}</PrimaryButton>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div role="radiogroup" aria-label="Standing" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {STANDINGS.map((s) => {
            const on = standing === s.value;
            const current = sub.standing === s.value;
            return (
              <button key={s.value} type="button" role="radio" aria-checked={on} disabled={current} onClick={() => setStanding(s.value)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9, textAlign: 'left', fontFamily: 'var(--font-sans)',
                cursor: current ? 'default' : 'pointer', background: on ? s.soft : 'var(--bg-surface)', opacity: current ? 0.6 : 1,
                border: `1px solid ${on ? `color-mix(in oklch, ${s.tone} 45%, transparent)` : 'var(--border-default)'}`,
              }}>
                <span style={{ width: 9, height: 9, borderRadius: 99, background: s.tone, flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: on ? s.tone : 'var(--text-1)' }}>{s.label}</span>
                {current && <span style={muted}>now</span>}
              </button>
            );
          })}
        </div>
        <Field id="standing-reason" as="textarea" label="Why" required={needsReason} value={reason} onChange={setReason}
          placeholder={needsReason ? 'What happened' : 'Optional'} />
      </div>
    </ModalShell>
  );
};

/* ── the page ───────────────────────────────────────────────────────────────────────────────── */

const SubcontractorDetailPage = ({ onOpenProject }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = ['info', 'documents', 'jobs', 'notes'].includes(params.get('tab')) ? params.get('tab') : 'info';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [changingStanding, setChangingStanding] = useState(false);
  const [docDialog, setDocDialog] = useState(null); // { mode: 'add', presetType } | { mode: 'edit', doc }
  const [jobDoc, setJobDoc] = useState(null); // a job's document: { mode: 'add', kind, job, replacing? } | { mode: 'edit', doc, job }
  const [focus, setFocus] = useState(null); // the Documents row a flag asked for: { key, seq }
  const [viewing, setViewing] = useState(null); // a document open in the viewer
  const [wording, setWording] = useState(null); // the COI wording, for its Copy
  const [taxId, setTaxId] = useState(null);
  const [taxBusy, setTaxBusy] = useState(false);
  const { confirm, confirmDialogProps } = useConfirm();

  const load = useCallback(async () => {
    try {
      const [d, w] = await Promise.all([getSubcontractor(id), getCoiWording().catch(() => null)]);
      setData(d);
      setWording((w && w.wording) || null);
      setError(null);
    } catch (e) {
      if (e.status === 404) setNotFound(true);
      else setError(e.message || 'Failed to load the subcontractor');
    }
  }, [id]);
  useEffect(() => { setData(null); setTaxId(null); setNotFound(false); load(); }, [load]);
  // A flag on the list or the dashboard opens its row here: ?tab=documents&focus=msa | w9 | prequal | jobs.
  const askedFocus = params.get('focus');
  useEffect(() => {
    if (!data || !askedFocus) return;
    const key = askedFocus === 'jobs' ? firstOwingKey(jobDocJobs(data.jobs, data.documents, todayYmd())) : askedFocus;
    setFocus((was) => ({ key, seq: (was ? was.seq : 0) + 1 }));
    setParams({ tab: 'documents' }, { replace: true });
  }, [data, askedFocus, setParams]);

  if (notFound) {
    return <StateScreen kind="empty" title="Subcontractor not found" body="It may have been a wrong link." />;
  }
  if (!data) {
    return error ? <div className="alert alert-error">{error}</div> : <div style={{ ...muted, padding: 16 }}>Loading…</div>;
  }
  const { subcontractor: sub, documents, notes, jobs } = data;
  const today = todayYmd();
  const f = onFile(documents);
  const jobDocs = jobDocJobs(jobs, documents, today);
  // The KPI band (Duncan 2026-10-21: the look of Job Costs): jobs, CE subcontracts, the last job, documents.
  const lastJob = lastJobOf(jobs, today); // "Now", the last date that has come, or "Next" (2026-10-24)
  const contracts = jobs.flatMap((j) => j.subcontracts);
  const firstContract = contracts.map((c) => c.contract_date).filter(Boolean).sort()[0] || null;
  const required = [['MSA', !!f.msa], ['W-9', !!f.w9]];
  const inOrder = required.filter(([, ok]) => ok).length;
  const missing = required.filter(([, ok]) => !ok).map(([name]) => name);
  const openJobs = jobDocs.filter((j) => j.open);
  const owing = openJobs.filter((j) => j.needs.length).length;
  const kpis = [
    { label: 'Jobs', value: jobs.length, sub: `${jobs.filter((j) => j.project_id).length} with a Summit page` },
    { label: 'CE subcontracts', value: contracts.length, sub: firstContract ? `since ${fmtMonthYear(firstContract)}` : 'none in CE' },
    { label: 'Last job', value: !lastJob ? '—' : lastJob.kind === 'now' ? 'Now' : lastJob.kind === 'next' ? `Next ${fmtMonthYear(lastJob.date)}` : fmtMonthYear(lastJob.date),
      valueColor: lastJob && lastJob.kind === 'now' ? 'var(--success)' : undefined, muted: !lastJob, sub: lastJob ? lastJob.job.project_name : 'no jobs yet' },
    {
      label: 'Company documents', value: `${inOrder} of 2`,
      valueColor: inOrder === 2 ? 'var(--success)' : inOrder === 1 ? 'var(--warning)' : 'var(--danger)',
      sub: missing.length ? `${missing.join(' and ')} missing` : 'MSA and W-9 on file',
    },
    {
      label: 'Job documents', value: openJobs.length ? `${openJobs.length - owing} of ${openJobs.length}` : '—', muted: !openJobs.length,
      valueColor: openJobs.length ? (owing ? 'var(--danger)' : 'var(--success)') : undefined,
      sub: !openJobs.length ? 'not on an open job' : owing ? `${owing} open job${owing === 1 ? '' : 's'} missing a COI or safety plan` : 'every open job in order',
    },
  ];

  const setTab = (t) => setParams(t === 'info' ? {} : { tab: t }, { replace: true });
  // A flag's row on the Documents tab, highlighted (Duncan 2026-10-22).
  const focusOn = (key) => { setTab('documents'); setFocus((was) => ({ key, seq: (was ? was.seq : 0) + 1 })); };
  const saveInfo = async (payload) => {
    const next = await updateSubcontractor(sub.id, payload);
    setData((d) => ({ ...d, subcontractor: next }));
    setTaxId(null); // a shown tax ID may have just changed
    setEditing(false);
    if (next.name !== sub.name) load(); // its job history follows the name
  };
  const saveStanding = async (standing, reason) => {
    const res = await setSubcontractorStanding(sub.id, standing, reason);
    setData((d) => ({ ...d, subcontractor: res.subcontractor, notes: [res.note, ...d.notes] }));
    setChangingStanding(false);
  };
  const toggleTaxId = async () => {
    if (taxId) return setTaxId(null);
    setTaxBusy(true);
    try {
      setTaxId((await getSubcontractorTaxId(sub.id)).tax_id);
    } catch (e) {
      setError(e.message || 'Failed to read the tax ID');
    } finally {
      setTaxBusy(false);
    }
  };
  const saveDoc = async (details, file) => {
    if (docDialog.mode === 'add') await uploadSubcontractorDocument(sub.id, file, details);
    else await updateSubcontractorDocument(docDialog.doc.id, Object.fromEntries(Object.entries(details).map(([k, v]) => [k, v === '' ? null : v])));
    setDocDialog(null);
    await load();
  };
  const saveJobDoc = async (details, file, scan) => {
    if (jobDoc.mode === 'add') await uploadJobDocument(jobDoc.job.id, sub.id, file, details, scan);
    else await updateSubcontractorDocument(jobDoc.doc.id, Object.fromEntries(Object.entries(details).map(([k, v]) => [k, v === '' ? null : v])));
    // Replace: the new one in, the old one off the list (its file kept in storage).
    if (jobDoc.replacing) await removeSubcontractorDocument(jobDoc.replacing.id).catch((e) => setError(e.message || 'The new one is in, but the old one couldn’t be removed'));
    setJobDoc(null);
    await load();
  };
  // A certificate's check, clicked: saved at once (its Undo is the same click back).
  const toggleCheck = async (doc, key, value) => {
    const next = await updateSubcontractorDocument(doc.id, { [key]: value });
    setData((d) => ({ ...d, documents: d.documents.map((x) => (x.id === doc.id ? { ...x, ...next } : x)) }));
  };
  const removeDoc = async (doc) => {
    const ok = await confirm({
      title: `Remove ${doc.title}?`,
      message: 'It comes off this list. The file is kept in storage.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await removeSubcontractorDocument(doc.id);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to remove the document');
    }
  };
  const download = async (doc) => {
    try {
      await downloadSubcontractorDocument(doc);
    } catch (e) {
      setError(e.message || 'Download failed');
    }
  };
  const addNote = async (kind, body) => {
    const note = await addSubcontractorNote(sub.id, kind, body);
    setData((d) => ({ ...d, notes: [note, ...d.notes] }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SubStyles />
      <style>{'@media (max-width: 700px) { .sub-info { grid-template-columns: minmax(0, 1fr) !important; } }'}</style>
      <button type="button" onClick={() => navigate('/subcontractors')} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, fontSize: 13.5, color: 'var(--text-2)', cursor: 'pointer' }}>
        <Icon name="arrow-left" size={14} />Subcontractors
      </button>
      {error && <div className="alert alert-error">{error}</div>}
      <section style={{ ...card, padding: 0, overflow: 'hidden' }} aria-label={sub.name}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 650, color: 'var(--text-1)', letterSpacing: '-0.015em' }}>{sub.name}</h2>
              <StandingPill standing={sub.standing} />
            </div>
            <div style={{ ...muted, fontSize: 13.5, marginTop: 4 }}>
              {[sub.trades.length ? sub.trades.join(', ') : 'No trades set', sub.since_year ? `since ${sub.since_year}` : null].filter(Boolean).join(' · ')}
            </div>
          </div>
          <SecondaryButton onClick={() => setChangingStanding(true)}>Standing</SecondaryButton>
          <SecondaryButton onClick={() => setEditing(true)}>Edit</SecondaryButton>
        </div>
        <RequiredChecklist f={f} jobDocs={jobDocs} onPick={(key) => focusOn(key === 'jobs' ? firstOwingKey(jobDocs) : key)} />
      </section>
      <KpiRow label={`${sub.name} at a glance`} tiles={kpis} />
      <section style={{ ...card, padding: 0, overflow: 'hidden' }} aria-label={`${sub.name} details`}>
        <div style={{ padding: '12px 16px' }}>
          <SegmentedControl value={tab} onChange={(t) => { setFocus(null); setTab(t); }} options={[
            { value: 'info', label: 'Info' },
            { value: 'documents', label: 'Documents', count: documents.length },
            { value: 'jobs', label: 'Job history', count: jobs.length },
            { value: 'notes', label: 'Notes', count: notes.length },
          ]} />
        </div>
        <div style={{ borderTop: '1px solid var(--border-subtle)' }}>
          {tab === 'info' && (
            <InfoTab sub={sub} documents={documents} taxId={taxId} onToggleTaxId={toggleTaxId} taxBusy={taxBusy}
              jobDocs={jobDocs} onSeeJobDocs={() => setTab('documents')} />
          )}
          {tab === 'documents' && (
            <DocumentsTab documents={documents} company={f} jobDocs={jobDocs} wording={wording} focus={focus}
              onAdd={(presetType, file) => setDocDialog({ mode: 'add', presetType, lock: ['msa', 'w9', 'prequal'].includes(presetType), file })}
              onAddJobDoc={(job, kind, file) => setJobDoc({ mode: 'add', kind, job, file })} onEditJobDoc={(job, doc) => setJobDoc({ mode: 'edit', doc, job })}
              onReplaceJobDoc={(job, doc) => setJobDoc({ mode: 'add', kind: doc.doc_type, job, replacing: doc })} onToggleCheck={toggleCheck}
              onOpenJob={(j) => navigate(`/projects/${j.slug || j.id}/subcontractors`)}
              onView={setViewing} onDownload={download} onEdit={(doc) => setDocDialog({ mode: 'edit', doc })} onRemove={removeDoc} />
          )}
          {tab === 'jobs' && <JobsTab sub={sub} jobs={jobs} onOpenProject={onOpenProject} />}
          {tab === 'notes' && <NotesTab notes={notes} onAdd={addNote} />}
        </div>
      </section>
      {editing && <SubcontractorForm sub={sub} onClose={() => setEditing(false)} onSubmit={saveInfo} />}
      {changingStanding && <StandingDialog sub={sub} onClose={() => setChangingStanding(false)} onSubmit={saveStanding} />}
      {viewing && <DocViewer doc={viewing} subtitle={`${viewing.title} · ${sub.name}`} onDownload={download} onClose={() => setViewing(null)} />}
      {docDialog && <CompanyDocDialog mode={docDialog.mode} doc={docDialog.doc} presetType={docDialog.presetType} lockType={!!docDialog.lock} subName={sub.name} initialFile={docDialog.file} onClose={() => setDocDialog(null)} onSubmit={saveDoc} />}
      {jobDoc && (
        <JobDocDialog mode={jobDoc.mode} kind={jobDoc.kind} doc={jobDoc.doc} job={jobDoc.job} projectId={jobDoc.job.id} subName={sub.name} replacing={jobDoc.replacing} initialFile={jobDoc.file}
          onClose={() => setJobDoc(null)} onSubmit={saveJobDoc} />
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default SubcontractorDetailPage;
